import { Flex, Input, useControllableState, UseControllableStateProps } from '@chakra-ui/react';
import { BaseButtonProps } from '@cks-internal/react-base/dist/BaseComponents/Buttons/BaseButton';
import { BaseButton } from '@cks-internal/react-base';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState } from 'react';

interface QuantityPickerProps extends UseControllableStateProps<number> {
  max?: number;
  min?: number;
  rootProps?: any;
}

export const QuantityPicker = (props: QuantityPickerProps) => {
  const { min = 0, max, ...rest } = props;

  const [value, setValue] = useControllableState(rest);
  const [inputValue, setInputValue] = useState(String(value));

  useEffect(() => {
    setInputValue(String(value));
  }, [value]);

  const handleDecrement = () => setValue(value === min ? value : value - 1);
  const handleIncrement = () => setValue(value === max ? value : value + 1);

  const commitValue = () => {
    const parsed = parseInt(inputValue, 10);
    if (isNaN(parsed) || parsed < min) {
      setValue(min);
    } else if (max !== undefined && parsed > max) {
      setValue(max);
    } else {
      setValue(parsed);
    }
  };

  return (
    <Flex borderRadius='base' borderWidth='1px' justifyContent='space-between' alignItems='center'>
      <QuantityPickerButton
        onClick={handleDecrement}
        icon={faMinus}
        disabled={value === min ? ['Min quantity reached'] : undefined}
        aria-label='Decrement'
      />
      <Input
        type='number'
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onBlur={commitValue}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commitValue();
        }}
        min={min}
        max={max}
        textAlign='center'
        fontWeight='medium'
        border='none'
        outline='none'
        _focus={{ boxShadow: 'none' }}
        minW='8'
        maxW='16'
        px='1'
        size='sm'
        css={{
          MozAppearance: 'textfield',
          '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
            WebkitAppearance: 'none',
            margin: 0
          }
        }}
        aria-label='Quantity'
      />
      <QuantityPickerButton
        onClick={handleIncrement}
        icon={faPlus}
        disabled={value === max ? ['Max quantity reached'] : undefined}
        aria-label='Increment'
      />
    </Flex>
  );
};

const QuantityPickerButton = (props: BaseButtonProps) => (
  <BaseButton
    size='sm'
    fontSize='md'
    iconProps={{
      color: 'gray.300'
    }}
    variant='ghost'
    _focus={{ boxShadow: 'none' }}
    _focusVisible={{ boxShadow: 'outline' }}
    {...props}
  />
);
